import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AssistantTopic, LiveFigure } from '@kortex/shared';
import { useTour } from './Tour';
import { api } from '../lib/api';

/** Where the help button docks: a slot the footer renders. */
export const HELP_SLOT_ID = 'assistant-slot';

interface LiveContext {
  lowStock: number;
  openPurchaseOrders: number;
  dirtyRooms: number;
  unbilledOnlineOrders: number;
  unpricedMenuItems: number;
  openTickets: number;
  trialDaysLeft: number | null;
}

interface Reply {
  understood: boolean;
  answers: AssistantTopic[];
  suggestions: AssistantTopic[];
  live: LiveContext;
}

interface Turn {
  id: number;
  question: string;
  reply?: Reply;
  failed?: boolean;
}

/**
 * A figure worth putting next to an answer.
 *
 * Only where it changes what somebody would do: telling them six things are
 * below reorder level as they ask how to raise a purchase order is help;
 * telling them zero rooms are dirty is noise.
 */
function liveLine(figure: LiveFigure | undefined, live: LiveContext): string | null {
  switch (figure) {
    case 'lowStock':
      return live.lowStock > 0
        ? live.lowStock === 1
          ? 'Right now 1 item is at or below its reorder level.'
          : `Right now ${live.lowStock} items are at or below their reorder level.`
        : null;
    case 'openPurchaseOrders':
      return live.openPurchaseOrders > 0
        ? `You have ${live.openPurchaseOrders} purchase order${live.openPurchaseOrders === 1 ? '' : 's'} in flight.`
        : null;
    case 'dirtyRooms':
      return live.dirtyRooms > 0
        ? `${live.dirtyRooms} room${live.dirtyRooms === 1 ? ' is' : 's are'} waiting to be cleaned.`
        : null;
    case 'unbilledOnlineOrders':
      return live.unbilledOnlineOrders > 0
        ? `${live.unbilledOnlineOrders} delivered order${live.unbilledOnlineOrders === 1 ? ' has' : 's have'} no invoice yet.`
        : null;
    case 'unpricedMenuItems':
      return live.unpricedMenuItems > 0
        ? `${live.unpricedMenuItems} dish${live.unpricedMenuItems === 1 ? '' : 'es'} on your menu still has no price.`
        : null;
    case 'openTickets':
      return live.openTickets > 0
        ? `${live.openTickets} ticket${live.openTickets === 1 ? ' is' : 's are'} open on the floor.`
        : null;
    case 'trialDaysLeft':
      return live.trialDaysLeft !== null
        ? `${live.trialDaysLeft} day${live.trialDaysLeft === 1 ? '' : 's'} left on your trial.`
        : null;
    default:
      return null;
  }
}

/**
 * Help that knows where you are and what you are allowed to do.
 *
 * It answers from a corpus written alongside the product rather than from a
 * language model: nothing about this hotel leaves the server, it cannot invent
 * a screen that does not exist, and it works on a hotel's own hardware with no
 * API key and no internet. What it will explain depends on the signed-in
 * person - the server filters by permission before anything comes back.
 */
export function Assistant() {
  const tour = useTour();
  const [open, setOpen] = useState(false);
  // Looked up after mount rather than during render: the footer that owns the
  // slot is a sibling, so it is not in the document on the first pass.
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [question, setQuestion] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const bottom = useRef<HTMLDivElement>(null);

  const opening = useQuery({
    queryKey: ['assistant-suggestions', location.pathname],
    queryFn: () =>
      api.get<{ suggestions: AssistantTopic[]; live: LiveContext }>(
        `/api/assistant/suggestions?route=${encodeURIComponent(location.pathname)}`,
      ),
    enabled: open,
    staleTime: 60_000,
  });

  const ask = useMutation({
    mutationFn: (asked: string) =>
      api.post<Reply>('/api/assistant/ask', { question: asked, route: location.pathname }),
    onSuccess: (reply, asked) =>
      setTurns((prev) => prev.map((t) => (t.question === asked && !t.reply ? { ...t, reply } : t))),
    onError: (_error, asked) =>
      setTurns((prev) =>
        prev.map((t) => (t.question === asked && !t.reply ? { ...t, failed: true } : t)),
      ),
  });

  useEffect(() => {
    setSlot(document.getElementById(HELP_SLOT_ID));
  }, []);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns, open]);

  const send = (asked: string) => {
    const text = asked.trim();
    if (!text) return;
    setTurns((prev) => [...prev, { id: prev.length, question: text }]);
    setQuestion('');
    ask.mutate(text);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    send(question);
  };

  const goTo = (route: string) => {
    navigate(route);
    setOpen(false);
  };

  const live = opening.data?.live;

  const launcher = (
    <button
      className={`assistant-launcher ${open ? 'open' : ''}`}
      onClick={() => setOpen((was) => !was)}
      aria-label={open ? 'Close help' : 'Open help'}
      aria-expanded={open}
    >
      {open ? 'Close' : 'Need Help?'}
    </button>
  );

  return (
    <>
      {/*
       * Docked into the footer rather than floating over the page.
       *
       * A floating button in the corner sits on top of whatever is under it,
       * and on the till that is the Bill button - which lives in a panel that
       * does not scroll, so it can never be moved out from underneath. The
       * footer is on screen at all times now, so the button has somewhere to be
       * that is always reachable and never covers anything.
       */}
      {slot ? createPortal(launcher, slot) : launcher}

      {open ? (
        <aside className="assistant" role="dialog" aria-label="Help">
          <header className="assistant-head">
            <div>
              <b>How can I help?</b>
              <div className="xs muted">Ask in your own words</div>
            </div>
            <button className="btn ghost icon sm" onClick={() => setOpen(false)} aria-label="Close">
              ✕
            </button>
          </header>

          <div className="assistant-body">
            {turns.length === 0 ? (
              <div className="assistant-intro">
                {/* First thing in the panel: somebody opening Help who does
                    not yet know what to ask wants the tour, not a text box. */}
                {tour.available ? (
                  <button
                    className="btn primary block"
                    onClick={() => {
                      setOpen(false);
                      tour.start();
                    }}
                  >
                    ▶ Product Tour — {tour.available.title}
                  </button>
                ) : null}
                <p className="small">
                  I know every screen in SARTHI and what you are allowed to open. Ask me how to do
                  something, and I will give you the steps and take you there.
                </p>
                {opening.data?.suggestions.length ? (
                  <>
                    <div className="xs muted" style={{ marginTop: 12 }}>
                      On this screen
                    </div>
                    <div className="assistant-chips">
                      {opening.data.suggestions.map((topic) => (
                        <button
                          key={topic.id}
                          className="assistant-chip"
                          onClick={() => send(topic.title)}
                        >
                          {topic.title}
                        </button>
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}

            {turns.map((turn) => (
              <div key={turn.id} className="assistant-turn">
                <div className="assistant-asked">{turn.question}</div>

                {turn.failed ? (
                  <div className="assistant-said">
                    <p className="small warn">
                      I could not reach the server just then. Try asking again.
                    </p>
                  </div>
                ) : !turn.reply ? (
                  <div className="assistant-said">
                    <p className="small muted">Looking…</p>
                  </div>
                ) : (
                  <div className="assistant-said">
                    {turn.reply.understood ? (
                      turn.reply.answers.map((topic) => {
                        const note = liveLine(topic.live, turn.reply!.live);
                        return (
                          <article key={topic.id} className="assistant-answer">
                            <b>{topic.title}</b>
                            <p className="small">{topic.summary}</p>
                            <ol className="assistant-steps">
                              {topic.steps.map((step) => (
                                <li key={step}>{step}</li>
                              ))}
                            </ol>
                            {note ? <p className="assistant-live">{note}</p> : null}
                            {topic.route ? (
                              <button className="btn primary sm" onClick={() => goTo(topic.route!)}>
                                {topic.routeLabel ?? 'Take me there'}
                              </button>
                            ) : null}
                          </article>
                        );
                      })
                    ) : (
                      <p className="small">
                        I do not know that one. I only answer about SARTHI itself, and I would
                        rather say so than invent something.
                      </p>
                    )}

                    {turn.reply.suggestions.length > 0 ? (
                      <>
                        <div className="xs muted" style={{ marginTop: 10 }}>
                          {turn.reply.understood ? 'Related' : 'Try one of these'}
                        </div>
                        <div className="assistant-chips">
                          {turn.reply.suggestions.map((topic) => (
                            <button
                              key={topic.id}
                              className="assistant-chip"
                              onClick={() => send(topic.title)}
                            >
                              {topic.title}
                            </button>
                          ))}
                        </div>
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
            <div ref={bottom} />
          </div>

          {live && turns.length === 0 ? (
            <div className="assistant-status">
              {[
                liveLine('lowStock', live),
                liveLine('dirtyRooms', live),
                liveLine('unbilledOnlineOrders', live),
                liveLine('unpricedMenuItems', live),
              ]
                .filter(Boolean)
                .slice(0, 2)
                .map((line) => (
                  <div key={line} className="xs">
                    {line}
                  </div>
                ))}
            </div>
          ) : null}

          <form className="assistant-ask" onSubmit={submit}>
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="How do I bill a table?"
              aria-label="Ask the assistant"
              maxLength={300}
            />
            <button className="btn primary sm" type="submit" disabled={!question.trim()}>
              Ask
            </button>
          </form>
        </aside>
      ) : null}
    </>
  );
}
